import styled from 'styled-components';

/* Pestañas de los paneles de promotores (promotor, empresa y admin). Se ven
   también en teléfono, donde el menú del encabezado está oculto. */
export const PanelTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;

  button {
    flex: 1 1 auto;
    min-width: 0;
    text-align: center;
    padding: 10px 16px;
    border-radius: var(--radius-round);
    border: 1.5px solid var(--border-medium);
    background: var(--bg-card);
    color: var(--text-secondary);
    font-family: var(--font-brand);
    font-size: var(--text-sm);
    font-weight: 600;
    cursor: pointer;
    transition: all var(--transition-fast);
  }

  button:hover { border-color: var(--secondary); color: var(--secondary); }

  button[aria-selected='true'] {
    background: var(--primary);
    border-color: var(--primary);
    color: var(--text-on-primary);
  }

  button:focus-visible {
    outline: 2px solid var(--border-focus);
    outline-offset: 2px;
  }
`;

/* Lista de tarjetas (relaciones, actividades, ventas, cortes, promotores). */
export const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

export const ItemCard = styled.article`
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xs);
  padding: 14px 16px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &[data-muted='true'] { background: var(--bg-section); }

  .head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px 12px;
    flex-wrap: wrap;
    min-width: 0;
  }

  .title {
    font-weight: 700;
    min-width: 0;
    overflow-wrap: anywhere;
    flex: 1 1 160px;
  }

  .meta {
    font-size: var(--text-sm);
    color: var(--text-secondary);
    overflow-wrap: anywhere;
    line-height: 1.45;
  }

  .note {
    font-size: var(--text-sm);
    color: var(--text-primary);
    background: var(--bg-section);
    border-left: 3px solid var(--secondary);
    border-radius: var(--radius-sm);
    padding: 8px 12px;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }

  .money {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
    align-items: baseline;
    font-size: var(--text-sm);
    color: var(--text-secondary);
  }

  .money strong { color: var(--secondary); font-size: var(--text-base); font-weight: 800; }

  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 2px;
  }

  @media (max-width: 460px) {
    .actions > * { flex: 1 1 100%; }
  }
`;

/* Tarjeta que abre un detalle (corte). */
export const ItemButton = styled.button`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  text-align: left;
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xs);
  padding: 14px 16px;
  font-family: var(--font-brand);
  color: var(--text-primary);
  cursor: pointer;
  min-width: 0;
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);

  &:hover { border-color: var(--secondary); box-shadow: var(--shadow-sm); }

  .head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px 12px;
    flex-wrap: wrap;
  }

  .title { font-weight: 700; min-width: 0; overflow-wrap: anywhere; flex: 1 1 160px; }
  .meta { font-size: var(--text-sm); color: var(--text-secondary); overflow-wrap: anywhere; line-height: 1.45; }

  .total {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    font-size: var(--text-sm);
    color: var(--text-secondary);
    padding-top: 8px;
    border-top: 1px solid var(--border-light);
  }

  .total strong { color: var(--secondary); font-size: var(--text-lg); font-weight: 800; }
  .total strong[data-negative='true'] { color: var(--danger-dark); }
`;

/* Pastillas pequeñas dentro de las tarjetas (origen, tardía, tipo...). */
export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: var(--text-xs);
  font-weight: 700;
  padding: 2px 10px;
  border-radius: var(--radius-round);
  background: var(--primary-subtle);
  color: var(--text-secondary);
  border: 1px solid var(--border-light);
  white-space: nowrap;
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;

  .stat {
    background: var(--primary-subtle);
    border-radius: var(--radius-md);
    padding: 14px 16px;
    min-width: 0;
  }

  .stat .value {
    font-size: var(--text-2xl);
    font-weight: 800;
    letter-spacing: -0.5px;
    color: var(--primary);
    overflow-wrap: anywhere;
  }

  .stat .label {
    font-size: var(--text-xs);
    font-weight: 600;
    color: var(--text-secondary);
  }

  .caption {
    grid-column: 1 / -1;
    font-size: var(--text-xs);
    color: var(--text-muted);
    line-height: 1.45;
  }

  @media (max-width: 460px) {
    gap: 8px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    .stat { padding: 12px 10px; }
    .stat .value { font-size: var(--text-xl); }
  }
`;

/* Fila de logo + nombre (empresas). */
export const EmpresaLine = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1 1 200px;

  .logo {
    width: 40px;
    height: 40px;
    border-radius: var(--radius-md);
    background: var(--primary-subtle);
    color: var(--primary);
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
  }

  .logo img { width: 100%; height: 100%; object-fit: cover; }

  .names { min-width: 0; }
  .names strong { display: block; overflow-wrap: anywhere; }
  .names span { font-size: var(--text-sm); color: var(--text-secondary); overflow-wrap: anywhere; }
`;

/* Enlace para compartir de un promotor. */
export const ShareBox = styled.div`
  background: var(--bg-section);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  padding: 12px;
  min-width: 0;

  .url {
    display: block;
    font-family: ui-monospace, Menlo, monospace;
    font-size: var(--text-xs);
    color: var(--text-primary);
    background: var(--white);
    border: 1px solid var(--border-light);
    border-radius: var(--radius-sm);
    padding: 8px 10px;
    margin-bottom: 10px;
    overflow-wrap: anywhere;
    word-break: break-all;
    user-select: all;
  }
`;

/* Aviso de estado (pendiente / rechazado / suspendido). */
export const StatusCard = styled.section`
  background: var(--bg-card);
  border: 1px solid ${props => (props.$tone === 'error' ? 'var(--danger-light)' : 'var(--warning-border)')};
  border-left: 5px solid ${props => (props.$tone === 'error' ? 'var(--danger)' : 'var(--warning)')};
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xs);
  padding: 22px;
  margin-bottom: 16px;
  min-width: 0;

  h2 { font-size: var(--text-xl); margin: 10px 0 8px; }
  p { color: var(--text-secondary); line-height: 1.55; overflow-wrap: anywhere; }

  @media (max-width: 560px) { padding: 16px; }
`;

export const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
`;
